import { Link, useParams } from "react-router-dom";
import {
  Band,
  Button,
  Hero,
  Note,
  Prose,
  Split,
  Wrap,
  contentsItem,
  coverFrame,
  sectionHead,
} from "../components/ui/index.ts";
import {
  amazonUrl,
  bookBySlug,
  COVER_WIDTHS,
  coverAlt,
  coverSrc,
  coverSrcSet,
  lineById,
} from "../data/books.ts";
import { helpPath } from "../lib/routes.ts";
import NotFound from "./NotFound.tsx";

/**
 * One book's own page.
 *
 * This is where the physical facts live — trim, binding, what is between the
 * covers. They were briefly on the front page, where they had no business
 * being: they describe a printed object, not a company, and they will differ
 * from book to book the moment a second format exists.
 */
export default function Book() {
  const { slug } = useParams();
  const book = slug ? bookBySlug(slug) : undefined;
  if (!book) return <NotFound />;

  const line = lineById(book.line);
  const buy = amazonUrl(book);

  const specs: Array<[string, string]> = [
    ["Difficulty", book.designation],
    ["Puzzles", String(book.puzzleCount)],
    ["Trim size", book.trim],
    ["Binding", book.binding],
    ...(book.pages ? ([["Pages", String(book.pages)]] as Array<[string, string]>) : []),
    ...(book.published
      ? ([["Published", formatDate(book.published)]] as Array<[string, string]>)
      : []),
    ["Checked", "Every puzzle verified before print"],
  ];

  return (
    <>
      <Hero
        eyebrow={
          <>
            <Link to="/catalogue">Catalogue</Link>
            {line ? (
              <>
                {" · "}
                <Link to={line.href}>{line.name}</Link>
              </>
            ) : null}
          </>
        }
        eyebrowClassName="[&_a]:text-inherit [&_a]:no-underline [&_a]:inline-block [&_a]:py-[0.25rem] [&_a:hover]:underline [&_a:hover]:underline-offset-[3px]"
        title={
          <>
            {book.title}
            {book.subtitle ? <>: {book.subtitle}</> : null}
          </>
        }
        lede={book.blurb}
        media={
          <img
            className={`${coverFrame} max-w-[300px] justify-self-center max-hero:max-w-[210px]`}
            src={coverSrc(book, COVER_WIDTHS[1])}
            srcSet={coverSrcSet(book)}
            // 860px is `--breakpoint-hero`, spelled out because a `sizes`
            // attribute is HTML and cannot read a CSS token. Moving the
            // breakpoint means moving this too.
            sizes="(min-width: 860px) 300px, 210px"
            width={COVER_WIDTHS[1]}
            height={Math.round((COVER_WIDTHS[1] * 110) / 85)}
            alt={coverAlt(book)}
          />
        }
        actions={
          <>
            {/*
              A book is archived as soon as it is built and listed on KDP some
              time after, so there is a real window where the site can help a
              reader with a book it cannot yet sell them. Saying so is better
              than a button that goes nowhere.
            */}
            {buy ? (
              <Button href={buy} rel="noreferrer noopener">
                Find it on Amazon
              </Button>
            ) : (
              <p className="text-muted text-minor m-0">Coming soon to Amazon.</p>
            )}
            {book.archived ? (
              <Button to={helpPath(book)} ghost>
                Stuck on a puzzle?
              </Button>
            ) : null}
          </>
        }
      />

      <Band>
        <Wrap>
          <Split>
            <Prose>
              <h2 className={sectionHead}>What is inside</h2>
              <ul className="m-0 p-0 list-none">
                {book.contents.map((item) => (
                  <li key={item} className={contentsItem}>
                    {item}
                  </li>
                ))}
              </ul>
            </Prose>

            <div>
              <h2 className={sectionHead}>The book itself</h2>
              <dl className="grid m-0 max-w-[30rem]">
                {specs.map(([term, value]) => (
                  <div
                    key={term}
                    className="border-t border-line py-[0.7rem] flex gap-4 items-baseline"
                  >
                    <dt className="flex-[0_0_8rem] text-[0.7rem] uppercase tracking-[0.13em] font-bold text-muted">
                      {term}
                    </dt>
                    <dd className="m-0 text-minor flex-1">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Split>
        </Wrap>
      </Band>

      {line ? (
        <Band variant="alt">
          <Wrap>
            <Note>
              <p className="mb-0">
                <strong>What a {line.name.toLowerCase().replace(/s$/, "")} book promises.</strong>{" "}
                Exactly one solution, reachable without guessing, with no clue you could remove.{" "}
                <Link to={line.href}>How we check that →</Link>
              </p>
            </Note>
          </Wrap>
        </Band>
      ) : null}
    </>
  );
}

function formatDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString("en-GB", { year: "numeric", month: "long", timeZone: "UTC" });
}
